import { useCallback, useState } from 'react';
import type { ResolvedSite } from '@bms2/shared';
import { PAGES_PATH, apiFetch } from './api';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

/**
 * The site cascade of the event window (T953): the lookup the Place field makes when a site is picked, held by
 * site so a second pick of the same site answers at once. 🔨 It returns the held answer, or null while one is
 * fetched, because the modal asks for it while it renders and a promise cannot be returned into that shape.
 * The Calendar page, the record's Add Event and the sender menu's New event all read it here.
 *
 * 🔒 `opening` names one opening of the event window: when it changes, every held answer is dropped, so a Site
 * read granted later shows the band the next time the window opens (T3562). The two record modals mount the hook
 * per opening and pass nothing; the Calendar page holds it for the page's life and passes the window's key.
 */
export function useSiteCascade(apiBaseUrl: string, opening: string | null = null): (siteId: string) => ResolvedSite | null {
  const [held, setHeld] = useState<{ opening: string | null; cascades: Record<string, ResolvedSite | null> }>({ opening, cascades: {} });
  // 🔨 Reset while rendering, not in an effect, so the first render of a new opening never reads the last one's refusal.
  const current = held.opening === opening ? held : { opening, cascades: {} };
  if (current !== held) setHeld(current);
  const { cascades } = current;
  return useCallback(
    (siteId: string): ResolvedSite | null => {
      if (siteId in cascades) return cascades[siteId] ?? null;
      const hold = (answer: ResolvedSite | null) =>
        // An answer that lands after its window closed belongs to no opening and is dropped.
        setHeld((was) => (was.opening === opening ? { opening, cascades: { ...was.cascades, [siteId]: answer } } : was));
      void apiFetch(`${apiBaseUrl}${PAGES_PATH}/calendar/cascade/${siteId}`)
        .then(async (response) => {
          // 🔨 A refusal (403, a viewer without Site read, decision 10350) is held as none, so the render that asks
          // again does not fire the read again. Any other failure holds nothing and the next ask retries.
          if (response.status === 403) return { refused: true as const };
          return { refused: false as const, body: response.ok ? ((await response.json()) as ResolvedSite | null) : null };
        })
        .then((answer) => {
          if (answer.refused) hold(null);
          else if (answer.body) hold(answer.body);
        })
        .catch((failed: unknown) => {
          reportCaughtInBrowser('records.siteCascade.read', failed);
        });
      return null;
    },
    [apiBaseUrl, cascades, opening],
  );
}
